import { THROWING_STAR_VISUALS as V } from '../../../../config/abilities/throwingStarVisuals.config';
import { throwingStarPoints } from '../../../rendering/shared/ThrowingStarShape';
import { abilityVfxColor } from '../../abilityPalette';
import type { VFXEffectConfig, VFXPrimitive } from '../../VFXEffectRegistry';

const metal = abilityVfxColor('throw_star') ?? V.metalColor;
const reloadPoints = throwingStarPoints(V.reload.starSize).flatMap(({ x, y }) => [x, y]);
const reloadBorderPoints = throwingStarPoints(V.reload.starSize + V.reload.strokeWidth * 2)
  .flatMap(({ x, y }) => [x, y]);

const reloadStars: VFXPrimitive[] = Array.from({ length: V.reload.stars }, (_, index): VFXPrimitive => ({
  type: 'polygon',
  polygonPoints: reloadPoints,
  color: V.highlightColor,
  alpha: V.reload.alpha,
  stroke: { color: V.edgeColor, width: V.reload.strokeWidth },
  offsetX: V.reload.centerOffsetX + Math.cos(index / V.reload.stars * Math.PI * 2) * V.reload.radius,
  offsetY: V.reload.centerOffsetY + Math.sin(index / V.reload.stars * Math.PI * 2) * V.reload.radius,
  orbit: {
    radius: V.reload.radius,
    speed: V.reload.turnsPerSecond,
    startAngle: index / V.reload.stars * Math.PI * 2,
    centerOffsetX: V.reload.centerOffsetX,
    centerOffsetY: V.reload.centerOffsetY,
  },
  tween: { rotation: Math.PI * 4, scale: V.reload.endScale, alpha: V.reload.endAlpha,
    duration: V.reload.durationMs, ease: 'Sine' },
})).flatMap((star): VFXPrimitive[] => [
  // ShapeSpawner's polygon fill has no stroke method: an explicit backing
  // silhouette keeps the dark outline visible on both terrain and sprites.
  { ...star, polygonPoints: reloadBorderPoints, color: V.edgeColor, depth: 100 },
  { ...star, depth: 101 },
]);

/** Physical steel stars: compact hand glint, real history trail, crisp contact. */
export const THROWING_STAR_VFX: VFXEffectConfig[] = [
  {
    id: 'star_throw', name: 'Throwing Star Release', category: 'burst', duration: V.throw.durationMs,
    primitives: [
      { type: 'arc', arcRadius: V.throw.radius, arcStartAngle: -75, arcEndAngle: 75,
        color: metal, alpha: V.throw.alpha, stroke: { color: metal, width: 1.5 },
        tween: { scale: 1.35, alpha: 0, duration: V.throw.durationMs } },
      { type: 'star', points: 4, innerRadius: 1, outerRadius: 4, color: V.highlightColor, alpha: 0.75,
        tween: { scale: 0.3, alpha: 0, duration: V.throw.durationMs } },
    ],
  },
  {
    id: 'star_impact', name: 'Throwing Star Contact', category: 'burst', duration: V.impact.durationMs,
    primitives: [
      { type: 'star', points: 4, innerRadius: 2, outerRadius: V.impact.radius,
        color: V.highlightColor, alpha: 0.85,
        tween: { scale: 0.3, rotation: Math.PI / 4, alpha: 0, duration: V.impact.durationMs } },
      ...Array.from({ length: 4 }, (_, index): VFXPrimitive => {
        const angle = Math.PI / 4 + index * Math.PI / 2;
        const x = Math.cos(angle) * V.impact.shardLength;
        const y = Math.sin(angle) * V.impact.shardLength;
        return { type: 'line', linePoints: [{ x: 0, y: 0 }, { x: x * 0.45, y: y * 0.45 }],
          lineWidth: V.impact.shardWidth, color: metal, alpha: 0.8,
          tween: { x, y, alpha: 0, duration: V.impact.durationMs, ease: 'Power2' } };
      }),
    ],
  },
  {
    id: 'star_trail', name: 'Throwing Star Wake', category: 'trail',
    primitives: [{ type: 'ribbon', color: metal, ribbonGlowColor: V.highlightColor,
      lineWidth: V.trail.width, alpha: V.trail.alpha, ribbonLengthMs: V.trail.durationMs,
      ribbonMaxPoints: V.trail.maxPoints, ribbonTaper: true, ribbonFade: true }],
  },
  {
    // Sole owner of the reload effect; legacy events resolve this same recipe.
    id: 'reload_stars', name: 'Reload Throwing Stars', category: 'burst', duration: V.reload.durationMs,
    description: 'Three steel stars gather beside the throwing hand while the magazine refills.',
    primitives: [
      { type: 'arc', arcRadius: V.reload.radius, arcStartAngle: -130, arcEndAngle: 130,
        offsetX: V.reload.centerOffsetX, offsetY: V.reload.centerOffsetY,
        color: metal, alpha: V.reload.ringAlpha, stroke: { color: metal, width: V.reload.strokeWidth },
        tween: { rotation: Math.PI * 2, alpha: 0, duration: V.reload.durationMs } },
      ...reloadStars,
    ],
  },
  {
    id: 'reload_stars_complete', name: 'Throwing Stars Ready', category: 'burst', duration: V.ready.durationMs,
    primitives: [
      { type: 'shockwave', shockwaveInnerRadius: V.ready.radius - 1.5, shockwaveOuterRadius: V.ready.radius,
        offsetX: V.reload.centerOffsetX, offsetY: V.reload.centerOffsetY,
        color: metal, alpha: 0.55, tween: { scale: 1.5, alpha: 0, duration: V.ready.durationMs } },
      { type: 'star', points: 4, innerRadius: 1.5, outerRadius: 6, color: V.highlightColor, alpha: 0.9,
        offsetX: V.reload.centerOffsetX, offsetY: V.reload.centerOffsetY,
        tween: { scale: 0.3, alpha: 0, duration: V.ready.durationMs } },
    ],
  },
];
